import { array, number, option, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import {
    Amount,
    CancellationPolicyTag,
    divideAmount,
    isZeroAmount,
    sumAmounts,
    UntaxedAmount,
    ZERO_AMOUNT,
} from '@mews-distributor/common';
import { CancellationFee } from '@mews-distributor/data-access';
import { getProp, getStringProp, values } from '@mews/fp';
import { DateTimeUtc, DateTimeUtcService } from '@mews/framework';
import { WithNowUtcProps } from '../../selectors/views/availabilityBlocks';
import { hasSelectedEnterpriseNetPricing } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { ApplicationState } from '../../types';
import { WithCategoryId } from '../../types/selectors/selectorPropTypes';
import { getCurrencyCode } from '../app/selectors';
import { getReservationsPrices } from '../entities/reservationPrices/selectors';
import {
    getCurrentDateServiceCategoryRatesPricing,
    getCurrentServiceDateCategoryPricing,
} from '../pricing/views/servicePricing';

const getCurrentDateServiceCategoryRateCancellationFees = createSelector(
    getCurrentServiceDateCategoryPricing,
    getStringProp('rateId'),
    (pricingValues, rateId) =>
        pipe(
            pricingValues,
            option.flatMap((pricingValue) => option.fromNullable(pricingValue?.rates?.[rateId]?.cancellationFees)),
            option.getOrElse<Array<CancellationFee>>(() => [])
        )
);

const byEndUtc = pipe(
    string.Ord,
    ord.contramap((a: CancellationFee) => a.endUtc)
);
const getStillValidFees = (nowUtc: DateTimeUtc) => (fees: CancellationFee[]) =>
    pipe(
        fees,
        array.sortBy([byEndUtc]),
        array.filter((fee) => DateTimeUtcService.isGreaterOrEqual(DateTimeUtcService.parse(fee.endUtc), nowUtc))
    );

const getCurrentFee = (fees: CancellationFee[], nowUtc: DateTimeUtc) =>
    pipe(
        option.fromNullable(fees),
        option.flatMap(flow(getStillValidFees(nowUtc), array.sortBy([byEndUtc]), array.head)),
        option.getOrElse<CancellationFee | null>(() => null)
    );

const getCurrentDateServiceCategoryRateCancellationPolicyFee = createSelector(
    getCurrentDateServiceCategoryRateCancellationFees,
    getProp<DateTimeUtc>()('nowUtc'),
    getCurrentFee
);

export const getCurrentDateServiceCategoryRateCancellationPolicyTagWithDate = createSelector(
    getCurrentDateServiceCategoryRateCancellationPolicyFee,
    getProp<UntaxedAmount>()('totalAmount'),
    hasSelectedEnterpriseNetPricing,
    (currentFee, totalAmount, hasNetPricing) => {
        if (currentFee == null) {
            return {
                cancellationPolicyTag: CancellationPolicyTag.NonRefundable,
                endDate: null,
            };
        }

        if (isZeroAmount(currentFee.amount)) {
            return {
                cancellationPolicyTag: CancellationPolicyTag.FreeCancellation,
                endDate: currentFee.endUtc,
            };
        }

        if (!hasNetPricing && currentFee.amount.grossValue < totalAmount.grossValue) {
            return {
                cancellationPolicyTag: CancellationPolicyTag.PartiallyRefundable,
                endDate: currentFee.endUtc,
            };
        }

        if (hasNetPricing && currentFee.amount.netValue < totalAmount.netValue) {
            return {
                cancellationPolicyTag: CancellationPolicyTag.PartiallyRefundable,
                endDate: currentFee.endUtc,
            };
        }

        return {
            cancellationPolicyTag: CancellationPolicyTag.NonRefundable,
            endDate: null,
        };
    }
);

const hasFreeCancellation = (fees: Array<CancellationFee>) =>
    pipe(
        fees,
        array.some((fee) => isZeroAmount(fee.amount))
    );

export const getCurrentDateServiceCategoryFreeRatesIds = createSelector(
    getCurrentDateServiceCategoryRatesPricing,
    getProp<DateTimeUtc>()('nowUtc'),
    (ratesPricing, nowUtc) =>
        pipe(
            ratesPricing,
            array.filterMap((pricingRate) => {
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                const stillActiveFees = pipe(pricingRate?.cancellationFees || [], getStillValidFees(nowUtc));
                if (hasFreeCancellation(stillActiveFees)) {
                    return option.some(pricingRate.id);
                }
                return option.none;
            })
        )
);

interface GetCancellationPolicyTagProps {
    fees: CancellationFee[];
    totalAmount: Amount | null;
    nowUtc: DateTimeUtc;
    hasNetPricing: boolean;
}
export const getCancellationPolicyTag = ({
    fees,
    totalAmount,
    nowUtc,
    hasNetPricing,
}: GetCancellationPolicyTagProps): CancellationPolicyTag => {
    const currentFee = getCurrentFee(fees, nowUtc);

    if (currentFee == null || totalAmount == null) {
        return CancellationPolicyTag.NonRefundable;
    }

    if (isZeroAmount(currentFee.amount)) {
        return CancellationPolicyTag.FreeCancellation;
    }

    if (!hasNetPricing && totalAmount.grossValue > currentFee.amount.grossValue) {
        return CancellationPolicyTag.PartiallyRefundable;
    }

    if (hasNetPricing && totalAmount.netValue > currentFee.amount.netValue) {
        return CancellationPolicyTag.PartiallyRefundable;
    }
    return CancellationPolicyTag.NonRefundable;
};

const cancellationPolicyTagsOrder = {
    [CancellationPolicyTag.FreeCancellation]: 1,
    [CancellationPolicyTag.PartiallyRefundable]: 2,
    [CancellationPolicyTag.NonRefundable]: 3,
};

const orderBySeverity = pipe(
    number.Ord,
    ord.contramap((tag: CancellationPolicyTag) => cancellationPolicyTagsOrder[tag])
);
const orderCancellationFeeTags = (tags: CancellationPolicyTag[]): CancellationPolicyTag[] =>
    pipe(tags, array.sortBy([orderBySeverity]), array.uniq<CancellationPolicyTag>(string.Eq));

export const getCurrentDateServiceCategoryPolicy = (
    state: ApplicationState,
    { categoryId, nowUtc }: WithCategoryId & WithNowUtcProps
) => {
    const hasNetPricing = hasSelectedEnterpriseNetPricing(state, {});
    const currencyCode = getCurrencyCode(state);
    return pipe(
        getCurrentDateServiceCategoryRatesPricing(state, { categoryId }),
        array.map((pricingValue) =>
            getCancellationPolicyTag({
                fees: pricingValue?.cancellationFees ?? [],
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                totalAmount: currencyCode ? pricingValue.price.totalAmount?.[currencyCode] : null,
                nowUtc,
                hasNetPricing,
            })
        ),
        orderCancellationFeeTags
    );
};

const filterRedundantPolicies =
    (nowUtc: DateTimeUtc, totalAmount: Amount) =>
    (fees: CancellationFee[]): CancellationFee[] =>
        pipe(
            fees,
            getStillValidFees(nowUtc),
            array.filter((fee) => fee.amount.grossValue < totalAmount.grossValue)
        );

export const getReservationsCancellationFees = createSelector(
    getReservationsPrices,
    getProp<DateTimeUtc>()('nowUtc'),
    getProp<Amount>()('totalAmount'),
    (reservationPrices, nowUtc, totalAmount) =>
        pipe(
            reservationPrices,
            array.flatMap((reservationPrice) => reservationPrice.cancellationFees),
            // @ts-expect-error discrepancy between custom amount and generated amount
            array.reduce<CancellationFee, Record<string, CancellationFee>>({}, (totalFees, cancellationFees) => ({
                ...totalFees,
                [cancellationFees.endUtc]: {
                    endUtc: cancellationFees.endUtc,
                    amount:
                        totalFees[cancellationFees.endUtc] != null
                            ? sumAmounts(totalFees[cancellationFees.endUtc].amount, cancellationFees.amount)
                            : cancellationFees.amount,
                },
            })),
            values,
            array.map(
                (fee) =>
                    ({
                        ...fee,
                        amount: divideAmount(fee.amount, array.size(reservationPrices)),
                    }) as unknown as CancellationFee
            ),
            filterRedundantPolicies(nowUtc, totalAmount)
        )
);

export const getReservationsTotalAmount = createSelector(getReservationsPrices, (reservationPrices) =>
    pipe(
        reservationPrices,
        array.map((reservationPrice) => reservationPrice.totalAmount),
        array.reduce<Amount, Amount>(ZERO_AMOUNT, sumAmounts)
    )
);
